import React, { useEffect } from "react";

import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { useLocation } from "@Libs/Router/hooks/useLocation";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import {
  AUTH_SCREEN_KEYS,
  SUBCATEGORY_SCREEN_KEY,
  isAuthBranchScreenKey,
} from "../../config/constants";
import { useTaskCreationFlow } from "../../Contexts/TaskCreationFlow";
import {
  DraftIdentityProvider,
  useDraftIdentity,
} from "../../hooks/useDraftIdentity";
import { useFlowCategoryParams } from "../../hooks/useFlowPCategory";
import { IServiceRequestDraft } from "../../types";
import { findFirstIncompleteScreen } from "../../utils/buildFlowShape";
import { AuthEmailScreen } from "../../Containers/AuthEmailScreen/AuthEmailScreen";
import { AuthLoginScreen } from "../../Containers/AuthLoginScreen/AuthLoginScreen";
import { AuthRegisterScreen } from "../../Containers/AuthRegisterScreen/AuthRegisterScreen";
import { DescriptionScreen } from "../../Containers/DescriptionScreen/DescriptionScreen";
import { DynamicScreen } from "../../Containers/DynamicScreen/DynamicScreen";
import { SubcategoryScreen } from "../../Containers/SubcategoryScreen/SubcategoryScreen";
import { TaskCreationShell } from "../../Containers/TaskCreationShell/TaskCreationShell";
import { serviceRequestPathFragment } from "@modules/ServiceRequest/config/routes";

/** The screens the frontend owns. Everything else comes from the config. */
const STATIC_SCREENS: Record<string, () => JSX.Element | null> = {
  [SUBCATEGORY_SCREEN_KEY]: SubcategoryScreen,
  [AUTH_SCREEN_KEYS.EMAIL]: AuthEmailScreen,
  [AUTH_SCREEN_KEYS.LOGIN]: AuthLoginScreen,
  [AUTH_SCREEN_KEYS.REGISTER]: AuthRegisterScreen,
};

/**
 * One screen of the flow, behind the mount guard.
 *
 * A visitor who arrived with an `s_category` already chosen (a SEM landing page)
 * never sees the picker: they are redirected to the first config screen, which
 * becomes the first screen of the flow they see. `replace` so the browser's back
 * button cannot walk them into a screen they were meant to skip.
 *
 * A step screen reached with no ongoing draft (typed URL, stale bookmark) has
 * nothing to render — there is no draft to fetch a config from — so
 * it redirects to the description screen instead of hanging on a blank page.
 *
 * TODO(RT-4321): the deep-link guard for an existing draft is deferred —
 * `findFirstIncompleteScreen` (`utils/buildFlowShape.ts`) still has no caller, so
 * a link to a screen the visitor has not reached (but has a draft for) is not
 * redirected to where they actually left off.
 */
const FlowScreen = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { screens, steps, answers, draft, isFlowLoading } =
    useTaskCreationFlow();
  // The URL's own key, not the flow's: before the config lands the flow reports
  // the picker whatever the path says, and a hard refresh mid-flow would then
  // render the picker over a config screen's URL.
  const { isEditing, draftKey, screenRoute, screenFragment } =
    useDraftIdentity();
  const { screenKey } = useParams([screenFragment]);
  // The SEM entry's own param, the same one the description screen reads. Only
  // a SEM landing sends it; the category picker sends `p_category` alone.
  const sCategory = useSearchParams("s_category");
  // Resolved, not read raw off the URL: the description screen ejects to the
  // Rails picker the moment its own URL carries no allowed `p_category`
  // (`DescriptionScreen`'s `hasCategory` gate), so a redirect that forwarded
  // only what this URL happened to hold turned into a two-hop bounce out of
  // the flow entirely — which reads as "back went straight to
  // /task_creation" (RT-4321).
  const categoryParams = useFlowCategoryParams();
  const hasDraft = Boolean(draftKey);

  useEffect(() => {
    if (!hasDraft)
      navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION, {
        withLocale: true,
        replace: true,
        queryParams: {
          ...categoryParams,
          ...(sCategory ? { s_category: sCategory } : {}),
        },
      });
  }, [hasDraft, categoryParams.p_category, sCategory]);

  // A screen key the flow shape does not carry — the URL and the shape have
  // gone out of sync, and the shape is the authority (RT-4321).
  //
  // Two ways in, both real. Changing the subcategory swaps the config for one
  // with different screen keys entirely (painter alone has `surface_type` and
  // `area`; every other category starts at `task_type`), so every URL and
  // history entry from before the switch names a screen that no longer
  // exists. And editing an answer a `visible_if` depends on can hide a
  // screen's every question, which drops the screen from the shape
  // (`buildFlowShape`) while the visitor is standing on it.
  //
  // Left alone the visitor is stranded: `currentScreenKey` reads `undefined`,
  // so the shell renders its no-step layout around a step screen and
  // `DynamicScreen` sits in its loading state for a screen it cannot find,
  // while Back — seeing nothing before an unknown key — leaves the flow
  // outright, skipping every step in between.
  //
  // `isFlowLoading` gates it: a key the shape has not caught up with *yet* is
  // not a stale key, and redirecting on it would fight a hard refresh.
  //
  // An auth branch is the one key absent from the shape on purpose — it hangs
  // off the email screen rather than sitting beside it (`buildFlowShape` lists
  // only `auth`) — so it is exempt. Read as stale, it was sent back to the
  // first incomplete screen the moment it mounted, which on the auth step is
  // the email screen it just came from: submitting an address looked like
  // nothing happening at all (RT-4321).
  const isStaleScreen =
    Boolean(screenKey) &&
    !isAuthBranchScreenKey(screenKey) &&
    !isFlowLoading &&
    screens.length > 0 &&
    !screens.some((screen) => screen.key === screenKey);
  // Where they actually left off, not the top of the flow: this is what
  // `findFirstIncompleteScreen` was written for — see its own comment on a
  // screen key being "safe to accept".
  const resumeScreen = isStaleScreen
    ? findFirstIncompleteScreen({ screens, steps }, answers, {
        isSubcategoryChosen: Boolean(draft?.s_category_id),
      })
    : undefined;

  useEffect(() => {
    if (resumeScreen)
      navigate(screenRoute(resumeScreen.key).fragments, {
        withLocale: true,
        params: screenRoute(resumeScreen.key).params,
        // `s_category` only when the URL already carried it: on a step URL its
        // presence is the SEM-entry marker, so this preserves what the visitor
        // arrived as without relabelling an ordinary screen.
        queryParams: {
          ...categoryParams,
          ...(sCategory ? { s_category: sCategory } : {}),
        },
        replace: true,
      });
  }, [resumeScreen?.key]);

  // Nothing to skip *to* until the config has arrived, so an early visit still
  // gets the picker rather than a blank page.
  const firstConfigScreen = screens.find(
    (screen) => screen.key !== SUBCATEGORY_SCREEN_KEY
  );
  // Only the picker is skipped — once the redirect has landed the visitor is
  // *on* the first config screen, still carrying the param, and must not be
  // redirected to it again.
  const skipTo =
    !isEditing && sCategory && screenKey === SUBCATEGORY_SCREEN_KEY
      ? firstConfigScreen?.key
      : undefined;

  useEffect(() => {
    if (skipTo && sCategory) {
      const pickerQuery = new URLSearchParams(search);
      pickerQuery.delete("s_category");
      const pickerSearch = pickerQuery.toString();

      window.history.replaceState(
        window.history.state,
        "",
        `${pathname}${pickerSearch ? `?${pickerSearch}` : ""}`
      );

      navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN, {
        withLocale: true,
        params: { screenKey: skipTo },
        // Forwarded, or the param dies here: `navigate` builds the path from
        // `params` alone, and the first config screen reads it to know it is a
        // SEM entry (it carries the prefill banner).
        // `p_category` too, or it dies at this hop and the description screen
        // ejects to the Rails picker on any later redirect back out (RT-4321).
        queryParams: { ...categoryParams, s_category: sCategory },
      });
    }
  }, [skipTo]);

  if (!hasDraft || skipTo || resumeScreen) return null;

  const StaticScreen = screenKey ? STATIC_SCREENS[screenKey] : undefined;

  return StaticScreen ? <StaticScreen /> : <DynamicScreen />;
};

/**
 * Client-side routes for the task creation flow.
 *
 * Rails serves this widget from `service_request/new` (`tasks_creation#new`)
 * under the `react_bare` layout. That layout does not mount `App.Router`, but
 * it does not need to: `WidgetRenderer` is wrapped in `withProviders`, so every
 * widget already renders inside the app-wide `BrowserRouter` and the global
 * route-fragment map. Hence `@Libs/Router` here rather than a second router.
 *
 * The entry path *is* the description screen — no separate `/description` route,
 * since a redirect would only drop the `p_category` query param the Rails picker
 * appends. Every screen after it hangs off `:screenKey`, which Rails also answers
 * (`config/routes/tasks.rb:30`) so a hard refresh mid-flow lands here again.
 *
 * `TaskCreationShell` wraps the routes rather than each screen: the header and
 * the summary are shared by the whole flow, and the shell is also what provides
 * the flow's progress, so any screen can read it without props.
 */
export interface IServiceRequestCreationRootProps {
  /**
   * Set by `tasks_creation#edit` only, and snake_case because that is what
   * lands: `react_helper` JSON-encodes the hash Rails gives it and
   * `WidgetRenderer` spreads the result straight onto this component.
   */
  task_id?: number;
  /** The task being edited, already serialized by `tasks_creation#edit`. */
  service_request?: IServiceRequestDraft;
}

export const ServiceRequestCreationRoot = ({
  task_id: taskId,
  service_request: serviceRequest,
}: IServiceRequestCreationRootProps) => (
  <DraftIdentityProvider serviceRequest={serviceRequest} taskId={taskId}>
    <TaskCreationShell>
      <Routes>
        <Route
          withLocale
          fragments={serviceRequestPathFragment.SERVICE_REQUEST_CREATION}
          element={<DescriptionScreen />}
        />
        <Route
          withLocale
          fragments={serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN}
          element={<FlowScreen />}
        />
        {/* The same screens, for a request that already exists. Rails serves
            both paths from `tasks_creation#edit` with this same widget. */}
        <Route
          withLocale
          fragments={serviceRequestPathFragment.SERVICE_REQUEST_EDIT}
          element={<DescriptionScreen />}
        />
        <Route
          withLocale
          fragments={serviceRequestPathFragment.SERVICE_REQUEST_EDIT_SCREEN}
          element={<FlowScreen />}
        />
      </Routes>
    </TaskCreationShell>
  </DraftIdentityProvider>
);
